import { List, useTable } from "@refinedev/antd";
import { CanAccess, useCan, useNotification } from "@refinedev/core";
import {
  Button,
  Checkbox,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { Link } from "react-router";

import { api } from "../api";
import { DATE_FORMAT, DATE_TIME_FORMAT, REVIEW_STATUS } from "../labels";
import type { ReviewItem } from "../types";

const STATUS_OPTIONS = [
  { value: "PENDING", label: "Bekleyen" },
  { value: "APPROVED", label: "Yayında" },
  { value: "REJECTED", label: "Reddedilen" },
  { value: "", label: "Tümü" },
];

const RATING_OPTIONS = [
  { value: "", label: "Tüm puanlar" },
  ...[1, 2, 3, 4, 5].map((rating) => ({ value: String(rating), label: `${rating} yıldız` })),
];

function filterValue(filters: ReturnType<typeof useTable>["filters"], field: string) {
  const filter = filters.find((item) => "field" in item && item.field === field);
  return (filter?.value as string | undefined) ?? "";
}

/**
 * /reviews: written comments waiting for a decision, lowest rating first (permission
 * `reviews.view`; deciding needs `reviews.moderate`). Students appear as on the tutor page.
 */
export function ReviewsPage() {
  const { open } = useNotification();
  const { data: canSeeTutors } = useCan({ resource: "tutors", action: "list" });
  const [busy, setBusy] = useState<string | null>(null);
  const { tableProps, tableQuery, filters, setFilters } = useTable<ReviewItem>({
    resource: "reviews",
    pagination: { mode: "server", pageSize: 25 },
    filters: { initial: [{ field: "status", operator: "eq", value: "PENDING" }] },
    syncWithLocation: true,
  });
  const values = {
    status: filterValue(filters, "status"),
    rating: filterValue(filters, "rating"),
    withComment: filterValue(filters, "withComment") || "true",
  };
  const setFilter = (field: keyof typeof values, value: string) =>
    setFilters(
      Object.entries({ ...values, [field]: value }).map(([name, current]) => ({
        field: name,
        operator: "eq" as const,
        value: current,
      })),
      "replace",
    );

  async function decide(review: ReviewItem, decision: "APPROVE" | "REJECT") {
    setBusy(review.id);
    try {
      await api(`/api/office/reviews/${review.id}/decision`, {
        method: "POST",
        body: { decision },
      });
      open?.({
        type: "success",
        message: decision === "APPROVE" ? "Yorum yayına alındı." : "Yorum reddedildi.",
      });
      await tableQuery.refetch();
    } catch (error) {
      open?.({ type: "error", message: error instanceof Error ? error.message : "Hata oluştu." });
    } finally {
      setBusy(null);
    }
  }

  return (
    <List title="Yorumlar">
      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          options={STATUS_OPTIONS}
          value={values.status}
          onChange={(value) => setFilter("status", String(value))}
        />
        <Select
          aria-label="Puan"
          options={RATING_OPTIONS}
          value={values.rating}
          onChange={(value: string) => setFilter("rating", value)}
          style={{ width: 140 }}
        />
        <Checkbox
          checked={values.withComment === "false"}
          onChange={(event) => setFilter("withComment", event.target.checked ? "false" : "true")}
        >
          Yorumsuz puanları da göster
        </Checkbox>
      </Space>
      <Table {...tableProps} rowKey="id" scroll={{ x: "max-content" }}>
        <Table.Column<ReviewItem>
          title="Puan"
          dataIndex="rating"
          render={(rating: number) => (
            // Read-only stars, announced as one value ("3 yıldız") rather than five radios.
            <span
              role="img"
              aria-label={`${rating} yıldız`}
              style={{ color: "#fadb14", letterSpacing: 2 }}
            >
              {"★".repeat(rating)}
              <span style={{ color: "#d9d9d9" }}>{"★".repeat(5 - rating)}</span>
            </span>
          )}
        />
        <Table.Column<ReviewItem>
          title="Yorum"
          dataIndex="comment"
          render={(comment: string | null) =>
            comment ? (
              <Typography.Paragraph
                ellipsis={{ rows: 3, expandable: true, symbol: "devamı" }}
                style={{ maxWidth: 420, marginBottom: 0 }}
              >
                {comment}
              </Typography.Paragraph>
            ) : (
              <Typography.Text type="secondary">Yorum yok</Typography.Text>
            )
          }
        />
        <Table.Column<ReviewItem>
          title="Eğitmen"
          dataIndex="tutor"
          render={(tutor: ReviewItem["tutor"]) =>
            canSeeTutors?.can ? <Link to={`/tutors/${tutor.id}`}>{tutor.name}</Link> : tutor.name
          }
        />
        <Table.Column<ReviewItem>
          title="Öğrenci / ders"
          dataIndex="student"
          render={(student: string, review) => (
            <Space direction="vertical" size={0}>
              <span>{student}</span>
              <Typography.Text type="secondary">
                {review.lesson.subject} · {dayjs(review.lesson.startsAt).format(DATE_FORMAT)}
              </Typography.Text>
            </Space>
          )}
        />
        <Table.Column<ReviewItem>
          title="Gönderildi"
          dataIndex="createdAt"
          render={(value: string) => dayjs(value).format(DATE_TIME_FORMAT)}
        />
        <Table.Column<ReviewItem>
          title="Durum"
          dataIndex="status"
          render={(value: string) => (
            <Tag color={REVIEW_STATUS[value]?.color}>{REVIEW_STATUS[value]?.label}</Tag>
          )}
        />
        <Table.Column<ReviewItem>
          title=""
          key="actions"
          render={(_, review) => (
            <CanAccess resource="reviews" action="moderate">
              <Space>
                {review.status !== "APPROVED" ? (
                  <Popconfirm
                    title="Yorum yayına alınsın mı?"
                    description="Eğitmen sayfasında herkese görünür."
                    okText="Yayına al"
                    cancelText="Vazgeç"
                    onConfirm={() => decide(review, "APPROVE")}
                  >
                    <Button size="small" type="primary" loading={busy === review.id}>
                      Onayla
                    </Button>
                  </Popconfirm>
                ) : null}
                {review.status !== "REJECTED" ? (
                  <Popconfirm
                    title="Yorum reddedilsin mi?"
                    description="Yorum gizlenir ve puanı ortalamadan çıkar."
                    okText="Reddet"
                    okButtonProps={{ danger: true }}
                    cancelText="Vazgeç"
                    onConfirm={() => decide(review, "REJECT")}
                  >
                    <Button size="small" danger loading={busy === review.id}>
                      Reddet
                    </Button>
                  </Popconfirm>
                ) : null}
              </Space>
            </CanAccess>
          )}
        />
      </Table>
    </List>
  );
}
